import { useEffect, useState } from 'react';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { ErrorPage } from '../../components/ErrorPage/ErrorPage';

import { BlankLoadingPage } from '../../views/BlankLoadingPage/BlankLoadingPage';
import { CheckCircle2 } from 'lucide-react';
import { Button, Alert, Card, CardContent } from '../../components/primitives';
import { FormInput } from '../../components/form';
import { ResultCard } from '../../components/ResultCard';
import { OnboardingLayout } from '../OnboardingLayout';
import { useConfig } from '../../hooks/useConfig';

/**
 * What to do about a link that will not work — the same thing in every case,
 * whether the code is missing, expired or already used. Shared with the verify
 * screen, which had its own copy of the problem.
 */
export const INVITATION_REMEDY =
  'Open the link from your invitation email again, or ask the shop to resend it.';


interface AcceptInvitationProps {
  initialValues?: any;
  error?: string;
  success?: boolean;
  invitationCode?: string;
}

export default function AcceptInvitation({
  initialValues: overrideInitialValues,
  error: overrideError,
  success: overrideSuccess,
  invitationCode: overrideInvitationCode,
}: AcceptInvitationProps) {

  const auth = useAuth();
  const config = useConfig();
  // The shop's own name, which the config now carries — this sentence used to
  // contain the literal string "store name".
  const shopName = config?.shopConfig?.shop?.name;

  interface AcceptInvitationValues {
    password: string;
    passwordVerify: string;
    terms: boolean;
  }

  const defaultInitialValues: AcceptInvitationValues = {
    password: "",
    passwordVerify: "",
    terms: false,
  };

  const initialValues = overrideInitialValues || defaultInitialValues;

  const [error] = useState<string | undefined>(overrideError);
  const [success, setSuccess] = useState<boolean>(overrideSuccess || false);
  const [invitationCode] = useState<string | undefined>(overrideInvitationCode || sessionStorage.getItem('invitationCode') || undefined);



  useEffect(() => {
    if (auth?.isLoggedIn) {
      auth.logout();
    }
  }, []);

  // No deferred navigate('/') here: acceptance signs the seller in and the
  // auth-state router swap lands on /complete-registration (the seller is not
  // registered yet, and the registration router has no '/' route — navigating
  // there rendered a blank page). Worse, the old effect was keyed on the auth
  // OBJECT (new identity every provider render) with no cleanup, so it kept
  // scheduling fresh navigate timers that fired mid-registration.

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const acceptInvitationValidation = Yup.object().shape({
    password: Yup.string()
      .min(8)
      .max(16)
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
        'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character'
      ),
    passwordVerify: Yup.string().oneOf(
      [Yup.ref('password'), null],
      'Passwords must match'
    ),
  });

  const handleOnSubmit = async (
    values: AcceptInvitationValues,
    actions: FormikHelpers<AcceptInvitationValues>
  ) => {
    await auth?.acceptInvitation({
      invitationCode: invitationCode as string,
      password: values.password,
    }).then(() => {
      setSuccess(true);
    }).catch((error: any) => {
      setSubmitError(error.message);
    });
    actions.setSubmitting(false);
  };

  const handleOnChange = () => {
    setSubmitError(undefined);
  };

  // One title and one remedy for every way a link fails to work. Naming the
  // mechanism ("the code is missing from the address") tells the reader
  // something they can do nothing with; what they can do is open the email
  // again. The failure branch used to print whatever came back from the API —
  // "Could not complete your registration." — which names the outcome the
  // reader can already see and leaves them on a dead end.
  if (!invitationCode) return <ErrorPage title="Your invitation link is not valid" message={INVITATION_REMEDY} link={{ label: "Go to sign in", url: "/login" }} />;
  if (auth?.isLoggedIn && !success) return <BlankLoadingPage message='Logging out of existing session...' />;
  if (error) return <ErrorPage title="Your invitation link is not valid" message={INVITATION_REMEDY} link={{ label: "Go to sign in", url: "/login" }} />;

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
        <ResultCard
          tone="success"
          icon={CheckCircle2}
          title="Invitation Accepted"
          description="Your invitation has been accepted. Taking you to the next step…"
          testId="invitation-accepted"
        />
      </div>
    );
  }

  return (
    <OnboardingLayout
      step="invitation"
      title="Accept Invitation"
      subtitle={
        shopName
          ? `${shopName} has invited you to sell on their marketplace. Choose a password to get started.`
          : 'You have been invited to sell on this marketplace. Choose a password to get started.'
      }
      railFooter={shopName ? <>Invited by <b className="text-foreground">{shopName}</b>.</> : undefined}
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Card>
        <CardContent className="p-6">
      {/*
        Said here, in the panel, and not only in the rail beside it: the two
        fields underneath are labelled "Password" and "Re-enter password",
        which is exactly what a sign-in form looks like. Somebody arriving
        from an invitation email can read that as being asked for a password
        they are supposed to already have, and they do not have one.
      */}
      <h2 className="text-lg font-semibold text-foreground">Create your password</h2>
      <p className="mb-5 mt-1 text-sm text-muted-foreground">
        This is a new account, so pick a password now. You will use it to sign in from here on.
      </p>
      <Formik
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        validationSchema={acceptInvitationValidation}
      >
        {(props: FormikProps<AcceptInvitationValues>) => (
          <Form className="space-y-5" onChange={handleOnChange}>
            <FormInput
              name="password"
              id="password"
              label="New password"
              type="password"
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              name="passwordVerify"
              id="passwordVerify"
              type="password"
              label="Re-enter password"
              required
              disabled={props.isSubmitting}
            />
            <Button className="w-full" size="lg" type="submit" disabled={props.isSubmitting}>
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Accept
            </Button>
          </Form>
        )}
      </Formik>
        </CardContent>
      </Card>
    </OnboardingLayout>
  );
}
